import React, { useEffect, useState, useRef } from "react";
import { RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { Formik } from "formik";
import { Form, Switch } from "formik-antd";
import { Page, setPage } from "features/appstate/AppStateSlice";
import {
  updateCustomLogoSetting,
  getCustomLogoSetting,
} from "./ClientCustomLogoSettingsActions";
import { clearCustomLogo } from "./ClientCustomLogoSettingsSlice";
import { CustomLogoUpdateForm, Client } from "api/sms-api";
import { Row, Col, Button, Spin, Divider } from "antd";
import { RootState } from "store/store";
import i18n from "i18n";
import SaveButton from "components/SaveButton";
import { CloseOutlined } from "@ant-design/icons";
import { getFileAsDataUrl } from "utils/files";

interface ClientCustomLogoSettingsProps {
  item?: Client;
  loading: boolean;
  updateCustomLogoSetting: (
    params: CustomLogoUpdateForm,
    file: File | null
  ) => void;
  updateLoading: boolean;
  getCustomLogoSetting: () => void;
  clearCustomLogo: () => void;
  setPage: (page: Page) => void;
}

const ClientCustomLogoSettings: React.FC<
  ClientCustomLogoSettingsProps & RouteComponentProps
> = (props) => {
  const [hideLogo, setHideLogo] = useState<boolean | undefined>(undefined);
  const [customLogoUrl, setCustomLogoUrl] = useState<string>("");
  const [customLogoFile, setCustomLogoFile] = useState<File | null>(null);
  const customLogoInputRef = useRef<HTMLInputElement>(null);
  const customLogoImgInputRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    props.setPage(Page.clientCustomLogoSettings);
  }, [props.setPage]);

  useEffect(() => {
    props.getCustomLogoSetting();
  }, [props.getCustomLogoSetting]);

  if (props.loading || props.updateLoading) {
    return <Spin />;
  }

  const handleSubmit = async (form: CustomLogoUpdateForm) => {
    if (!customLogoImgInputRef.current) {
      form.customLogoUrl = undefined;
    } else {
      form.customLogoUrl = customLogoImgInputRef.current.src;
    }
    await props.updateCustomLogoSetting(form, customLogoFile);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    // Get the file contents as a Base64 data URL string for the preview
    const result = await getFileAsDataUrl(file);

    if (result) {
      setCustomLogoUrl(result);
    }
    // Store the file for upload later
    setCustomLogoFile(file);
  };

  const handleFileRemove = async () => {
    if (customLogoInputRef.current) {
      customLogoInputRef.current.value = "";
    }
    setCustomLogoUrl("");
    await props.clearCustomLogo();
  };

  const handleHideLogoChange = (checked: boolean) => {
    setHideLogo(checked);
  };

  const displayLogoUrl = props.item?.customLogoUrl ?? customLogoUrl;
  const displayHideLogo = props.item?.hideLogo ?? hideLogo;

  const initialValues: CustomLogoUpdateForm = {
    hideLogo: displayHideLogo,
    hideLowerAnswerImage: props.item?.hideLowerAnswerImage,
  };

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {() => {
          return (
            <Form layout="vertical">
              <Row justify="start">
                <Col span={18}>
                  <Form.Item name="hideLogo" label={i18n.t("client.hideLogo")}>
                    <Switch name="hideLogo" onChange={handleHideLogoChange} />
                  </Form.Item>
                  <Form.Item
                    name="customLogoUrl"
                    label={i18n.t("client.customLogoUrl")}
                  >
                    <input
                      type="file"
                      onChange={handleFileChange}
                      ref={customLogoInputRef}
                    />
                    {displayLogoUrl && (
                      <Row align="top">
                        <img
                          style={{ marginTop: "1em", maxWidth: "90%" }}
                          src={displayLogoUrl}
                          ref={customLogoImgInputRef}
                        />
                        <Button
                          shape="circle"
                          icon={<CloseOutlined />}
                          type="text"
                          style={{ marginTop: "0.5em" }}
                          onClick={handleFileRemove}
                        />
                      </Row>
                    )}
                  </Form.Item>
                  <Divider />
                  <Form.Item
                    name="hideLowerAnswerImage"
                    label={i18n.t("client.hideLowerAnswerImage")}
                  >
                    <Switch name="hideLowerAnswerImage" />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" style={{ marginTop: "2.5em" }}>
                <SaveButton />
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    item: state.clientCustomLogoSettings.item,
    loading: state.clientCustomLogoSettings.getLoading,
    updateLoading: state.clientCustomLogoSettings.updateLoading,
  };
};

const mapDispatchToProps = {
  setPage,
  updateCustomLogoSetting,
  getCustomLogoSetting,
  clearCustomLogo,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ClientCustomLogoSettings);
